// crm.service — poin (earn dari omzet, redeem) + guard.
import { currentUser } from '@core/session';
import { getSetting } from '@core/settings';
import * as repo from './crm.repo';
import { batchPoin, saldoPoin, batchPemberian, type SaldoPoin } from '@domain/loyalty';
import { todayISO } from '@domain/format';

export const canCRM = (): boolean => { const u = currentUser(); return !!u && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role); };
const poinCfg = (): { rate: number; berlakuBulan: number; ambangKtp: number } => {
  const c = getSetting<{ rate?: number; berlakuBulan?: number; ambangKtp?: number }>('poin', {});
  return { rate: Math.max(1, c.rate ?? 10000), berlakuBulan: Math.max(0, c.berlakuBulan ?? 12), ambangKtp: Math.max(0, c.ambangKtp ?? 500) };
};
export const poinRate = (): number => poinCfg().rate;
export const poinBerlakuBulan = (): number => poinCfg().berlakuBulan;
export const poinAmbangKtp = (): number => poinCfg().ambangKtp;
/** Porsi poin SERVIS saat menukar (%); sisanya diambil dari poin afiliasi. Hanya pencatatan. */
export const porsiServisPersen = (): number => {
  const v = getSetting<{ porsiServisPersen?: number }>('poin', {}).porsiServisPersen;
  return Math.min(100, Math.max(0, v ?? 70));
};

/** Saldo dipecah per sumber — untuk pencatatan penukaran (pelanggan tetap melihat satu angka). */
export function saldoPerSumber(
  per: { no: string; tgl: string; nilai: number }[],
  ditukar: number,
  grants: { id: number; tgl: string; poin: number; alasan: string; ditarik: boolean; berlakuBulan: number }[] = [],
  hariIni = todayISO(),
): { servis: number; afiliasi: number; total: number } {
  // Penukaran lama dibebankan ke poin servis lebih dulu (poin afiliasi baru ada belakangan),
  // supaya angka afiliasi tak pernah tampak lebih besar dari yang sebenarnya diberikan.
  const sServis = saldoPoin(batchPoin(per, poinRate(), poinBerlakuBulan()), ditukar, hariIni);
  const sisaTukar = Math.max(0, ditukar - (sServis.totalDiperoleh - sServis.totalHangus));
  const sAfil = saldoPoin(batchPemberian(grants), sisaTukar, hariIni);
  return { servis: sServis.tersedia, afiliasi: sAfil.tersedia, total: sServis.tersedia + sAfil.tersedia };
}

/** Poin diperoleh = floor(LTV / rate). Dipakai layar yang hanya butuh total (tanpa masa berlaku). */
export const pointsEarned = (ltv: number): number => Math.floor(ltv / poinRate());

/**
 * Saldo poin dengan MASA BERLAKU: dihitung per servis dari tanggal unit diambil.
 * Satu-satunya rumus yang boleh dipakai layar kasir maupun halaman poin pelanggan —
 * kalau dihitung dua kali dengan cara berbeda, angkanya akan berselisih dan pelanggan protes.
 */
export function saldo(
  per: { no: string; tgl: string; nilai: number }[],
  ditukar: number,
  hariIni = todayISO(),
  /** Poin PEMBERIAN (bonus afiliasi/koreksi) — ikut jadi saldo, punya masa berlakunya sendiri. */
  grants: { id: number; tgl: string; poin: number; alasan: string; ditarik: boolean; berlakuBulan: number }[] = [],
): SaldoPoin {
  return saldoPoin([...batchPoin(per, poinRate(), poinBerlakuBulan()), ...batchPemberian(grants)], ditukar, hariIni);
}

export async function redeem(customerId: number, points: number, note: string): Promise<void> {
  if (!canCRM()) throw new Error('Tidak punya izin menukar poin.');
  if (points <= 0) throw new Error('Jumlah poin tidak valid.');
  // Hitung ulang saldo SEGAR dari DB (jangan percaya snapshot render — cegah over-redeem basi).
  const [spend, redeemed] = await Promise.all([repo.spendByCustomer(), repo.redeemedByCustomer()]);
  const avail = Math.max(0, pointsEarned(spend.get(customerId)?.ltv ?? 0) - (redeemed.get(customerId) ?? 0));
  if (points > avail) throw new Error(`Poin melebihi saldo tersedia (${avail}).`);
  await repo.addRedemption({ customer_id: customerId, outlet: currentUser()?.outlet ?? null, points, note, by_user: currentUser()?.nama ?? '' });
}
